import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Activity, Award, BookOpen, Target } from "lucide-react";
import { KpiTile, EnrollmentItem, UpcomingDrills, ForceSnapshot, RecertAlert } from "./dashboard/Widgets";
import ThreatBriefWidget from "./dashboard/ThreatBriefWidget";
import SentinelBriefingStrip from "./dashboard/SentinelBriefingStrip";

export default function Dashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [recert, setRecert] = useState(null);

  useEffect(() => {
    (async () => {
      const [e, c, ex, m, r] = await Promise.all([
        api.get("/me/enrollments"),
        api.get("/me/certifications"),
        api.get("/exercises"),
        api.get("/metrics/summary"),
        api.get("/me/recertifications"),
      ]);
      setEnrollments(e.data); setCerts(c.data); setExercises(ex.data); setMetrics(m.data); setRecert(r.data);
    })();
  }, []);

  const myExercises = useMemo(
    () => exercises.filter((x) => x.track === user?.track || x.track === "all").slice(0, 3),
    [exercises, user?.track]
  );
  const inProgress = useMemo(() => enrollments.filter((e) => e.status !== "completed"), [enrollments]);
  const avgProgress = useMemo(() => (
    enrollments.length
      ? Math.round(enrollments.reduce((a, b) => a + (b.progress || 0), 0) / enrollments.length)
      : 0
  ), [enrollments]);

  const kpis = [
    { k: "Avg Progress", v: `${avgProgress}%`, Icon: Activity, color: "var(--cyan)" },
    { k: "Active Courses", v: inProgress.length, Icon: BookOpen, color: "var(--amber)" },
    { k: "Certifications", v: certs.length, Icon: Award, color: "var(--green)" },
    { k: "Exercises Queued", v: myExercises.length, Icon: Target, color: "var(--red)" },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline">// MISSION_BRIEF / {new Date().toISOString().slice(0, 10)}</div>
            <h1 className="font-display font-bold text-4xl uppercase mt-2" data-testid="dash-title">
              Welcome, <span className="text-[var(--cyan)]">{user?.name}</span>
            </h1>
            <div className="font-mono text-xs text-[var(--text-2)] tracking-widest uppercase mt-1">
              Track: {user?.track} &middot; Role: {user?.role}
            </div>
          </div>
          <Link to="/assistant" className="btn-ghost" data-testid="dash-sentinel-cta">
            Open Sentinel AI →
          </Link>
        </div>

        <div className="mb-6"><SentinelBriefingStrip /></div>

        <div className="mb-6"><ThreatBriefWidget /></div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {kpis.map((kpi) => (
            <KpiTile
              key={kpi.k}
              label={kpi.k}
              value={kpi.v}
              color={kpi.color}
              Icon={kpi.Icon}
              testId={`kpi-${kpi.k.replace(/\s+/g, "-").toLowerCase()}`}
            />
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flat-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="overline">// 01_ACTIVE_TRAINING</div>
                <h2 className="font-display font-bold text-2xl uppercase mt-1">Active Courses</h2>
              </div>
              <Link to="/courses" className="text-xs font-mono tracking-widest uppercase text-[var(--cyan)] hover:underline" data-testid="dash-all-courses">All Courses →</Link>
            </div>
            <div className="space-y-4">
              {enrollments.length === 0 && (
                <div className="text-[var(--text-2)] text-sm">No active courses. Visit the Courses page to enroll.</div>
              )}
              {enrollments.slice(0, 5).map((e) => (
                <EnrollmentItem key={e.id} enrollment={e} />
              ))}
            </div>
          </div>

          <UpcomingDrills exercises={myExercises} />
        </div>

        {recert && (recert.pending_certs?.length > 0 || recert.recertification_enrollments?.length > 0) && (
          <div className="mt-6"><RecertAlert data={recert} /></div>
        )}

        {metrics && <div className="mt-8"><ForceSnapshot metrics={metrics} /></div>}
      </main>
    </div>
  );
}
